import React from 'react';
import AdjustIcon from '@mui/icons-material/Adjust';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Divider } from '@mui/material';

import { hasProjectHierarchyFeatureFlag } from '@paragon/login-tools';

import { useAppMonitor } from '../../../../monitor';
import { ensureLevelIsVisible, hideLevelByGuid } from '../../../../store/actions/levelVisibilityActions';
import { handleStandardSceneItemEditPropertiesAction } from '../../../../store/actions/sceneInteractionActions';
import {
  getActiveLayoutLevelGuidOrNull,
  getAllLevelsDictionary,
  getVisibleLevelGuids,
} from '../../../../store/selectors/layoutDataSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { AnalyzeAllChangedMenuItem } from '../shared-menu-items/AnalyzeAllChangedMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';
import { LockOptionsSubMenu } from '../shared-menu-items/LockOptionsSubMenu';
import { SettingsSubMenu } from '../shared-menu-items/SettingsSubMenu';

export function LevelMenu(props: { guid: string }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const level = useAppSelector((state) => getAllLevelsDictionary(state)[props.guid]);
  const projectHierarchyModificationEnabled = useAppSelector(hasProjectHierarchyFeatureFlag);

  const isLevelVisible = useAppSelector((state) => getVisibleLevelGuids(state).has(props.guid));
  const isLevelActive = useAppSelector((state) => getActiveLayoutLevelGuidOrNull(state) === props.guid);

  if (level == null) return null;

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={() => {
          dispatch(handleStandardSceneItemEditPropertiesAction('level', level));
          monitor.recordInteraction('Level => Edit Properties menu item clicked');
        }}
      />

      <Divider />

      <SceneMenuItem
        text={isLevelVisible ? 'Hide Level' : 'Show Level'}
        icon={isLevelVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
        onClick={() => {
          if (isLevelVisible) {
            dispatch(hideLevelByGuid(props.guid));
            monitor.recordInteraction('Level => Hide Level menu item clicked');
          } else {
            dispatch(ensureLevelIsVisible(props.guid));
            monitor.recordInteraction('Level => Show Level menu item clicked');
          }
        }}
      />

      <SceneMenuItem
        text="Make Active Level"
        icon={<StarIcon />}
        disabled={isLevelActive}
        onClick={() => {
          dispatch(ensureLevelIsVisible(props.guid, { makeActive: true }));
          monitor.recordInteraction('Level => Make Active Level menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Show Only This Level"
        icon={<AdjustIcon />}
        onClick={() => {
          dispatch(ensureLevelIsVisible(props.guid, { makeActive: true, exclusive: true }));
          monitor.recordInteraction('Level => Show Only This Level menu item clicked');
        }}
      />

      <Divider />

      <SettingsSubMenu entry={{ type: 'level', item: level }} />
      <LockOptionsSubMenu type="level" guid={props.guid} />

      <Divider />

      <AnalyzeAllChangedMenuItem type="level" modelGuid={props.guid} />

      {projectHierarchyModificationEnabled && (
        <>
          <Divider />

          <DeleteMenuItem type="level" item={level} />
        </>
      )}
    </SceneMenuContent>
  );
}
